import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, TextField, Typography,
} from '@mui/material'
import EditIcon       from '@mui/icons-material/EditOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined'
import { supabase } from '../../supabaseClient'
import { readFunctionError } from '../../lib/functionError'

interface Props {
  projectId:    string
  stepNumber:   number
  /** Friendly title for the step, e.g. "Step 1: Topic Selection" */
  stepLabel:    string
  /** Fires after the reflection is loaded (with current saved state) and after each save. */
  onSavedChange?: (saved: boolean) => void
}

const REFLECTION_MIN = 100

/**
 * Reflection capture card. Used at the close of each step so students write
 * what they learned in their own voice — the prose that ends up in their
 * printed journal. Acknowledgments captured alongside (people, mentor, AI).
 *
 * Loads existing reflection on mount; switches between "view" mode (after
 * save) and "edit" mode (initial, or when student clicks Edit).
 */
export default function StepReflectionCard({ projectId, stepNumber, stepLabel, onSavedChange }: Props) {
  const [reflection,      setReflection]      = useState('')
  const [acknowledgments, setAcknowledgments] = useState('')
  const [loaded,          setLoaded]          = useState(false)
  const [saved,           setSaved]           = useState(false)
  const [editing,         setEditing]         = useState(true)
  const [saving,          setSaving]          = useState(false)
  const [error,           setError]           = useState<string | null>(null)

  // Load existing
  useEffect(() => {
    let cancelled = false
    supabase
      .from('step_reflection')
      .select('reflection, acknowledgments')
      .eq('project_id', projectId)
      .eq('step_number', stepNumber)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return
        const wasSaved = !!data
        if (data) {
          setReflection(data.reflection ?? '')
          setAcknowledgments(data.acknowledgments ?? '')
          setSaved(true)
          setEditing(false)
        }
        setLoaded(true)
        onSavedChange?.(wasSaved)
      })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, stepNumber])

  async function handleSave() {
    if (reflection.trim().length < REFLECTION_MIN) {
      setError(`Your reflection should be at least ${REFLECTION_MIN} characters — take a few minutes to think about it.`)
      return
    }
    setSaving(true)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('save-step-reflection', {
      body: {
        project_id:      projectId,
        step_number:     stepNumber,
        reflection:      reflection.trim(),
        acknowledgments: acknowledgments.trim() || null,
        ai_use_note:     null,
      },
    })
    if (invokeErr) { setError(await readFunctionError(invokeErr)); setSaving(false); return }
    if (data?.error) { setError(data.error); setSaving(false); return }
    setSaving(false)
    setSaved(true)
    setEditing(false)
    onSavedChange?.(true)
  }

  if (!loaded) {
    return <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
  }

  // ── Read-only saved view ───────────────────────────────────────────────────
  if (saved && !editing) {
    return (
      <Box sx={{
        mt: 3, p: 3, borderRadius: 2,
        bgcolor: 'rgba(46,125,50,0.04)',
        border: '1px solid', borderColor: 'success.light',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.dark' }}>
            Reflection saved for {stepLabel}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small" variant="text"
            startIcon={<EditIcon fontSize="small" />}
            onClick={() => setEditing(true)}
            sx={{ color: 'text.secondary' }}
          >
            Edit
          </Button>
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
          What I learned
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
          {reflection}
        </Typography>
        {acknowledgments && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
              Acknowledgments
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
              {acknowledgments}
            </Typography>
          </>
        )}
      </Box>
    )
  }

  // ── Edit mode ──────────────────────────────────────────────────────────────
  return (
    <Box sx={{
      mt: 3, p: 3, borderRadius: 2,
      bgcolor: 'rgba(25,118,210,0.04)',
      border: '1px solid', borderColor: 'primary.light',
    }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        Write your reflection for {stepLabel}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
        Take five minutes. What did you learn at this step? What surprised you? What would you do
        differently? This goes in your research journal — what your mentor and judges will read.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 0.5 }}>
        What I learned
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={5}
        value={reflection}
        onChange={e => setReflection(e.target.value)}
        placeholder="At the start of this step I thought… But after exploring, I realized… What surprised me was…"
        helperText={
          reflection.trim().length < REFLECTION_MIN
            ? `${reflection.trim().length} / ${REFLECTION_MIN} minimum`
            : 'Looks good — your mentor and judges will read this'
        }
        error={reflection.trim().length > 0 && reflection.trim().length < REFLECTION_MIN}
        sx={{ mb: 2 }}
      />

      <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 0.5 }}>
        Who and what helped me <Typography component="span" variant="caption" color="text.secondary">(optional, but recommended)</Typography>
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
        Mention people, mentors, family, the AI Coach, books, anything that helped you think.
        Real researchers acknowledge their contributors.
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={3}
        value={acknowledgments}
        onChange={e => setAcknowledgments(e.target.value)}
        placeholder="My mentor pushed back on my first idea about climate change being too broad. The AI Coach helped me think through equipment trade-offs. My mom drove me to the bee yard…"
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || reflection.trim().length < REFLECTION_MIN}
        >
          {saving
            ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Saving...</>
            : saved ? 'Save changes' : 'Save reflection'}
        </Button>
        {saved && (
          <Button variant="text" onClick={() => setEditing(false)} disabled={saving}>
            Cancel
          </Button>
        )}
      </Box>
    </Box>
  )
}
